import React from 'react';
import PropTypes from 'prop-types';
import SectionHeader from '../SectionHeader';
import CrewCard from '../../../../CrewsPage/components/CrewCard/CrewCard';
import CrewCardSkeleton from '../../../../CrewsPage/components/CrewCardSkeleton';
import ScrollControls from '../../../../../components/ScrollControls/ScrollControls';
import css from './CrewsSection.module.css';

/**
 * CrewsSection component - displays crews using the reusable CrewCard component
 * Matches the structure from CrewsPage for consistency
 */
const CrewsSection = ({
  title,
  catchyPhrase = null,
  description = null,
  crews,
  onCrewClick,
  onJoinCrew,
  joinedCrews = [],
  user = null,
  showScrollControls = false,
  scrollRef = null,
  keyPrefix = 'crew',
  loading = false,
  iconSrc = null,
  iconAlt = '',
  cardTheme = 'light',
  joinCrewButtonClassName = null,
}) => {
  return (
    <section className={css.section}>
      <SectionHeader title={title} user={user} iconSrc={iconSrc} iconAlt={iconAlt} />
      {(catchyPhrase || description) && (
        <div className={css.crewsSubtitle}>
          {catchyPhrase && <span className={css.catchyPhrase}>{catchyPhrase}</span>}
          {description && <p className={css.sectionDescription}>{description}</p>}
        </div>
      )}
      {showScrollControls && scrollRef && (
        <div className={css.scrollControlsContainer}>
          <ScrollControls containerRef={scrollRef} theme="dark" />
        </div>
      )}
      <div ref={scrollRef} className={css.crewsGrid}>
        {loading
          ? Array.from({ length: 5 }, (_, i) => (
              <CrewCardSkeleton key={`${keyPrefix}-skeleton-${i}`} />
            ))
          : crews.map(crew => (
              <CrewCard
                key={`${keyPrefix}-${crew.id}`}
                crew={crew}
                onClick={onCrewClick}
                onJoinCrew={onJoinCrew}
                isJoined={joinedCrews.includes(crew.id)}
                theme={cardTheme}
                joinCrewButtonClassName={joinCrewButtonClassName}
              />
            ))}
      </div>
    </section>
  );
};

CrewsSection.propTypes = {
  title: PropTypes.string.isRequired,
  catchyPhrase: PropTypes.string,
  description: PropTypes.string,
  crews: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      fans: PropTypes.string.isRequired,
      image: PropTypes.string.isRequired,
    })
  ).isRequired,
  onCrewClick: PropTypes.func.isRequired,
  onJoinCrew: PropTypes.func.isRequired,
  joinedCrews: PropTypes.arrayOf(PropTypes.string),
  user: PropTypes.object,
  showScrollControls: PropTypes.bool,
  scrollRef: PropTypes.object,
  keyPrefix: PropTypes.string,
  loading: PropTypes.bool,
  iconSrc: PropTypes.string,
  iconAlt: PropTypes.string,
  cardTheme: PropTypes.oneOf(['light', 'dark']),
  joinCrewButtonClassName: PropTypes.string,
};

export default CrewsSection;
